import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ZapIcon, ShieldCheckIcon, CodeIcon, DatabaseIcon, ActivityIcon, CheckCircle2Icon, ChevronDownIcon, AlertCircleIcon } from 'lucide-react';
import { PayKitCopilot } from '../components/copilot/PayKitCopilot';
export function HomePage() {
  return <>
      <div {...{}} data-id="element-572" />
      <section className="pt-20 pb-16 md:pt-28 md:pb-24 px-4 overflow-hidden relative" data-id="element-573">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-pk-indigo-light/40 via-background to-background -z-10" data-id="element-574"></div>
        <div className="container mx-auto max-w-6xl" data-id="element-575">
          <PayKitCopilot data-id="element-576" />
        </div>
      </section>
      {/* Social Proof */}
      <section className="py-10 border-y border-border bg-pk-surface/30" data-id="element-577">
        <div className="container mx-auto px-4 text-center" data-id="element-578">
          <p className="text-sm font-medium text-muted-foreground mb-6 uppercase tracking-wider" data-id="element-579">
            Trusted by builders at
          </p>
          <div className="flex flex-wrap justify-center gap-8 md:gap-16 opacity-50 grayscale" data-id="element-580">
            {/* Logos placeholders */}
            <div className="text-xl font-bold font-serif" data-id="element-581">Acme Corp</div>
            <div className="text-xl font-bold tracking-tighter" data-id="element-582">Globex</div>
            <div className="text-xl font-bold italic" data-id="element-583">Soylent</div>
            <div className="text-xl font-bold" data-id="element-584">Initech</div>
            <div className="text-xl font-bold font-mono" data-id="element-585">Umbrella</div>
          </div>
        </div>
      </section>
      {/* How It Works */}
      <section className="py-24 px-4" data-id="element-586">
        <div className="container mx-auto max-w-5xl" data-id="element-587">
          <div className="text-center mb-16" data-id="element-588">
            <h2 className="text-3xl font-bold mb-4" data-id="element-589">How PayKit works</h2>
            <p className="text-lg text-muted-foreground" data-id="element-590">
              From idea to fully configured Stripe account in three steps.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative" data-id="element-591">
            <div className="hidden md:block absolute top-12 left-[16%] right-[16%] h-0.5 bg-border -z-10" data-id="element-592"></div>

            <div className="bg-card border border-border rounded-xl p-8 text-center relative" data-id="element-593">
              <div className="w-16 h-16 mx-auto bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xl font-bold mb-6 shadow-lg shadow-primary/20" data-id="element-594">
                1
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-595">Answer questions</h3>
              <p className="text-muted-foreground" data-id="element-596">
                Tell us about your product, pricing model, and tech stack in our
                guided wizard.
              </p>
            </div>

            <div className="bg-card border border-border rounded-xl p-8 text-center relative" data-id="element-597">
              <div className="w-16 h-16 mx-auto bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xl font-bold mb-6 shadow-lg shadow-primary/20" data-id="element-598">
                2
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-599">Review stack</h3>
              <p className="text-muted-foreground" data-id="element-600">
                Get a tailored recommendation for Stripe modules, webhooks, and
                data models.
              </p>
            </div>

            <div className="bg-card border border-border rounded-xl p-8 text-center relative" data-id="element-601">
              <div className="w-16 h-16 mx-auto bg-primary text-primary-foreground rounded-full flex items-center justify-center text-xl font-bold mb-6 shadow-lg shadow-primary/20" data-id="element-602">
                3
              </div>

              <h3 className="text-xl font-semibold mb-3" data-id="element-603">Provision & Build</h3>
              <p className="text-muted-foreground" data-id="element-604">
                We configure Stripe for you and generate an implementation pack
                to start coding.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Feature Grid */}
      <section id="features" className="py-24 px-4 bg-pk-surface" data-id="element-605">
        <div className="container mx-auto max-w-6xl" data-id="element-606">
          <div className="text-center mb-16" data-id="element-607">
            <h2 className="text-3xl font-bold mb-4" data-id="element-608">
              Everything you need to build payments right
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto" data-id="element-609">
              We handle the Stripe complexity so you can focus on your product.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-610">
            {[{
            icon: ZapIcon,
            title: 'AI-Guided Setup',
            desc: 'Our expert system recommends the perfect Stripe configuration based on your specific business model.'
          }, {
            icon: ShieldCheckIcon,
            title: 'Stripe Best Practices',
            desc: "Avoid common pitfalls. We configure products, prices, and settings following Stripe's latest guidelines."
          }, {
            icon: ActivityIcon,
            title: 'Webhook Configuration',
            desc: 'We automatically create webhook endpoints and subscribe to the exact events your app needs.'
          }, {
            icon: CodeIcon,
            title: 'Implementation Pack',
            desc: 'Get a tailored bundle of environment variables, object IDs, and code snippets for your stack.'
          }, {
            icon: DatabaseIcon,
            title: 'Database Schema',
            desc: 'Receive a suggested SQL schema to store subscriptions, customers, and invoices in your own database.'
          }, {
            icon: AlertCircleIcon,
            title: 'Monitoring & Alerts',
            desc: 'Optional add-on to monitor your Stripe account for configuration drift and webhook failures.'
          }].map((feature, i) => <div key={i} className="bg-background border border-border rounded-xl p-6 hover:shadow-md transition-shadow" data-id="element-611">
                <div className="w-12 h-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4" data-id="element-612">
                  <feature.icon className="w-6 h-6" data-id="element-613" />
                </div>
                <h3 className="text-xl font-semibold mb-2" data-id="element-614">{feature.title}</h3>
                <p className="text-muted-foreground leading-relaxed" data-id="element-615">
                  {feature.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>
      {/* Pricing Teaser */}
      <section className="py-24 px-4" data-id="element-616">
        <div className="container mx-auto max-w-5xl" data-id="element-617">
          <div className="text-center mb-16" data-id="element-618">
            <h2 className="text-3xl font-bold mb-4" data-id="element-619">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-muted-foreground" data-id="element-620">
              Pay once for setup, optional monitoring for peace of mind.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-621">
            <div className="border border-border rounded-2xl p-8 bg-card flex flex-col" data-id="element-622">
              <h3 className="text-2xl font-bold mb-2" data-id="element-623">Free</h3>

              <p className="text-muted-foreground mb-6" data-id="element-624">
                Configuration & Recommendation
              </p>
              <div className="text-4xl font-bold mb-8" data-id="element-625">$0</div>
              <ul className="space-y-4 mb-8 flex-1" data-id="element-626">
                <li className="flex items-start gap-3" data-id="element-627">
                  <CheckCircle2Icon className="w-5 h-5 text-pk-success shrink-0" data-id="element-628" />
                  <span data-id="element-629">Guided questionnaire</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-630">
                  <CheckCircle2Icon className="w-5 h-5 text-pk-success shrink-0" data-id="element-631" />
                  <span data-id="element-632">Architecture recommendation</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-633">
                  <CheckCircle2Icon className="w-5 h-5 text-pk-success shrink-0" data-id="element-634" />
                  <span data-id="element-635">Preview Stripe modules</span>
                </li>
              </ul>
              <Link to="/setup" className="w-full block text-center bg-muted text-foreground hover:bg-muted/80 px-6 py-3 rounded-lg font-medium transition-colors" data-id="element-636">
                Start Free Setup
              </Link>
            </div>

            <div className="border-2 border-primary rounded-2xl p-8 bg-card flex flex-col relative shadow-xl" data-id="element-637">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-primary text-primary-foreground px-4 py-1 rounded-full text-sm font-medium" data-id="element-638">
                Most Popular
              </div>
              <h3 className="text-2xl font-bold mb-2" data-id="element-639">Setup</h3>

              <p className="text-muted-foreground mb-6" data-id="element-640">
                Provisioning & Implementation Pack
              </p>
              <div className="flex items-baseline gap-2 mb-8" data-id="element-641">
                <span className="text-4xl font-bold" data-id="element-642">$29</span>
                <span className="text-muted-foreground" data-id="element-643">one-time</span>
              </div>

              <ul className="space-y-4 mb-8 flex-1" data-id="element-644">
                <li className="flex items-start gap-3" data-id="element-645">
                  <CheckCircle2Icon className="w-5 h-5 text-primary shrink-0" data-id="element-646" />
                  <span data-id="element-647">Everything in Free</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-648">
                  <CheckCircle2Icon className="w-5 h-5 text-primary shrink-0" data-id="element-649" />
                  <span data-id="element-650">Automated Stripe provisioning</span>
                </li>
                <li className="flex items-start gap-3" data-id="element-651">
                  <CheckCircle2Icon className="w-5 h-5 text-primary shrink-0" data-id="element-652" />
                  <span data-id="element-653">Custom implementation pack</span>
                </li>

                <li className="flex items-start gap-3" data-id="element-654">
                  <CheckCircle2Icon className="w-5 h-5 text-primary shrink-0" data-id="element-655" />
                  <span data-id="element-656">Database schema & code snippets</span>
                </li>
              </ul>
              <Link to="/setup" className="w-full block text-center bg-primary text-primary-foreground hover:opacity-90 px-6 py-3 rounded-lg font-medium transition-opacity" data-id="element-657">
                Get Started
              </Link>
            </div>
          </div>

          <div className="mt-12 text-center" data-id="element-658">
            <p className="text-muted-foreground mb-4" data-id="element-659">
              Need ongoing webhook health and drift alerts?
            </p>
            <Link to="/pricing" className="text-primary font-medium hover:underline inline-flex items-center gap-1" data-id="element-660">
              View monitoring plans <ArrowRightIcon className="w-4 h-4" data-id="element-661" />
            </Link>
          </div>
        </div>
      </section>
      {/* FAQ */}
      <section className="py-24 px-4 bg-pk-surface" data-id="element-662">
        <div className="container mx-auto max-w-3xl" data-id="element-663">
          <div className="text-center mb-12" data-id="element-664">
            <h2 className="text-3xl font-bold mb-4" data-id="element-665">
              Frequently asked questions
            </h2>
          </div>

          <div className="space-y-4" data-id="element-666">
            {[{
            q: 'Do I need a Stripe account first?',
            a: 'Yes, you need to create a free Stripe account. During the provisioning step, you will securely connect your Stripe account to PayKit so we can configure it.'
          }, {
            q: 'Does PayKit process payments?',
            a: "No. PayKit is a configuration tool. All payments are processed directly by Stripe. We never see or touch your customers' payment data."
          }, {
            q: 'What happens if I change my pricing later?',
            a: "You can update your pricing directly in Stripe at any time. If you use our Monitoring add-on, we'll alert you to configuration drift and suggest updates."
          }, {
            q: 'Is the implementation pack specific to my framework?',
            a: 'Yes! We tailor the code snippets, environment variables, and database schema to the frontend and backend stack you select in the wizard.'
          }].map((faq, i) => <div key={i} className="bg-background border border-border rounded-lg p-6" data-id="element-667">
                <h3 className="text-lg font-semibold mb-2 flex justify-between items-center" data-id="element-668">
                  {faq.q}
                </h3>
                <p className="text-muted-foreground" data-id="element-669">{faq.a}</p>
              </div>)}
          </div>
        </div>
      </section>
      {/* Final CTA */}
      <section className="py-24 px-4 relative overflow-hidden bg-foreground" data-id="element-670">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-white/10 via-transparent to-transparent" data-id="element-671"></div>
        <div className="container mx-auto max-w-4xl text-center relative z-10" data-id="element-672">
          <h2 className="text-4xl font-bold mb-6 text-white" data-id="element-673">
            Ready to launch your payments?
          </h2>
          <p className="text-xl text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-674">
            Stop reading Stripe docs and start building your product. Get your
            custom implementation pack today.
          </p>
          <Link to="/setup" className="inline-flex items-center gap-2 bg-white text-foreground hover:bg-white/90 px-8 py-4 rounded-lg text-lg font-bold transition-colors shadow-xl" data-id="element-675">
            Start free setup
            <ArrowRightIcon className="w-5 h-5" data-id="element-676" />
          </Link>
        </div>
      </section>
    </>;
}